import { createAsyncThunk, type ThunkDispatch, type UnknownAction } from '@reduxjs/toolkit';
import {
  SPREADSHEET_VALIDATION_FAILED,
  type SpreadsheetValidationError,
} from '@/shared/utils/parseSpreadsheetValidationError';
import { downloadBlob } from '@/shared/utils/requestBuilder';
import {
  CobrancaDto,
  ConteudoDisparo,
  CorrecaoNomesLoteResult,
  CriarCobrancaInput,
  CriarCobrancaRejeicao,
  DuplicarReguaPayload,
  EditarLoteItemInput,
  EncerrarCobrancaInput,
  ExportarCobrancasParams,
  IniciarLoteResult,
  ListReguaCobrancasParams,
  ListReguaTemplatesParams,
  LoteItem,
  LoteItemStatus,
  MapeamentoLoteDto,
  ReenviarDisparoInput,
  ReenvioDisparoResultado,
  ReguaCobrancaDetalhe,
  ReguaCobrancaEtapas,
  ReguaCobrancaFinanceiro,
  ReguaCobrancaListResponse,
  ReguaCobrancaLog,
  ReguaTemplate,
  ReguaTemplateListResponse,
  SalvarReguaPayload,
  SalvarReguaResult,
  UploadLoteRejectPayload,
  ValidarInicioResult,
} from '../types';
import {
  type CobrancaAcaoLote,
  CobrancaConflitoError,
  reguaCobrancaService,
  reguaLoteService,
  reguaTemplateService,
} from './services';

export const listarReguaTemplates = createAsyncThunk<
  ReguaTemplateListResponse,
  ListReguaTemplatesParams | undefined,
  { rejectValue: string }
>('reguaCobranca/listarTemplates', async (params, { rejectWithValue }) => {
  try {
    return await reguaTemplateService.list(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao listar réguas de cobrança';
    return rejectWithValue(message);
  }
});

export const buscarReguaTemplate = createAsyncThunk<ReguaTemplate, string, { rejectValue: string }>(
  'reguaCobranca/buscarTemplate',
  async (id, { rejectWithValue }) => {
    try {
      return await reguaTemplateService.getById(id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao buscar régua de cobrança';
      return rejectWithValue(message);
    }
  },
);

export const listarReguaCobrancas = createAsyncThunk<
  ReguaCobrancaListResponse,
  ListReguaCobrancasParams
>('reguaCobranca/listarCobrancas', async (params, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.list(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao listar cobranças';
    return rejectWithValue(message);
  }
});

export const criarCobrancaIndividual = createAsyncThunk<
  CobrancaDto,
  CriarCobrancaInput,
  { rejectValue: CriarCobrancaRejeicao }
>('reguaCobranca/criarCobranca', async (input, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.criar(input);
  } catch (error) {
    if (error instanceof CobrancaConflitoError) {
      return rejectWithValue({ tipo: 'conflito', mensagem: error.message });
    }
    const mensagem = error instanceof Error ? error.message : 'Erro ao criar cobrança';
    return rejectWithValue({ tipo: 'erro', mensagem });
  }
});

export const salvarReguaTemplate = createAsyncThunk<
  SalvarReguaResult,
  { id: string; payload: SalvarReguaPayload },
  { rejectValue: string }
>('reguaCobranca/salvarTemplate', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await reguaTemplateService.salvar(id, payload);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao salvar régua de cobrança';
    return rejectWithValue(message);
  }
});

export const buscarReguaCobranca = createAsyncThunk<ReguaCobrancaDetalhe, string>(
  'reguaCobranca/buscarCobranca',
  async (id, { rejectWithValue }) => {
    try {
      return await reguaCobrancaService.getById(id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao buscar cobrança';
      return rejectWithValue(message);
    }
  },
);

export const buscarCobrancaEtapas = createAsyncThunk<
  ReguaCobrancaEtapas,
  string,
  { rejectValue: string }
>('reguaCobranca/buscarCobrancaEtapas', async (id, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.etapas(id);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar as etapas';
    return rejectWithValue(message);
  }
});

export const buscarCobrancaLogs = createAsyncThunk<
  ReguaCobrancaLog[],
  string,
  { rejectValue: string }
>('reguaCobranca/buscarCobrancaLogs', async (id, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.logs(id);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar os logs';
    return rejectWithValue(message);
  }
});

export const buscarCobrancaFinanceiro = createAsyncThunk<
  ReguaCobrancaFinanceiro,
  string,
  { rejectValue: string }
>('reguaCobranca/buscarCobrancaFinanceiro', async (id, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.financeiro(id);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar o financeiro';
    return rejectWithValue(message);
  }
});

type ReguaDispatch = ThunkDispatch<unknown, unknown, UnknownAction>;

async function recarregarCobranca(id: string, dispatch: ReguaDispatch): Promise<void> {
  await Promise.all([
    dispatch(buscarReguaCobranca(id)),
    dispatch(buscarCobrancaEtapas(id)),
    dispatch(buscarCobrancaLogs(id)),
    dispatch(buscarCobrancaFinanceiro(id)),
  ]);
}

export const suspenderEtapaCobranca = createAsyncThunk<
  void,
  { id: string; etapaChave: string; motivo?: string },
  { rejectValue: string }
>(
  'reguaCobranca/suspenderEtapa',
  async ({ id, etapaChave, motivo }, { dispatch, rejectWithValue }) => {
    try {
      await reguaCobrancaService.suspenderEtapa(id, etapaChave, motivo);
      await recarregarCobranca(id, dispatch);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao suspender a etapa';
      return rejectWithValue(message);
    }
  },
);

export const retomarEtapaCobranca = createAsyncThunk<
  void,
  { id: string; etapaChave: string },
  { rejectValue: string }
>('reguaCobranca/retomarEtapa', async ({ id, etapaChave }, { dispatch, rejectWithValue }) => {
  try {
    await reguaCobrancaService.retomarEtapa(id, etapaChave);
    await recarregarCobranca(id, dispatch);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao retomar a etapa';
    return rejectWithValue(message);
  }
});

export const reenviarDisparoCobranca = createAsyncThunk<
  ReenvioDisparoResultado,
  { id: string; disparoId: string; input: ReenviarDisparoInput },
  { rejectValue: string }
>(
  'reguaCobranca/reenviarDisparo',
  async ({ id, disparoId, input }, { dispatch, rejectWithValue }) => {
    try {
      const resultado = await reguaCobrancaService.reenviarDisparo(id, disparoId, input);
      await recarregarCobranca(id, dispatch);
      return resultado;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao reenviar o disparo';
      return rejectWithValue(message);
    }
  },
);

export const buscarConteudoDisparo = createAsyncThunk<
  ConteudoDisparo,
  { id: string; disparoId: string },
  { rejectValue: string }
>('reguaCobranca/buscarConteudoDisparo', async ({ id, disparoId }, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.conteudoDisparo(id, disparoId);
  } catch (error) {
    const message =
      error instanceof Error ? error.message : 'Erro ao carregar o conteúdo do disparo';
    return rejectWithValue(message);
  }
});

export const pausarCobranca = createAsyncThunk<
  void,
  { id: string; motivo?: string },
  { rejectValue: string }
>('reguaCobranca/pausarCobranca', async ({ id, motivo }, { dispatch, rejectWithValue }) => {
  try {
    await reguaCobrancaService.pausar(id, motivo);
    await recarregarCobranca(id, dispatch);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao pausar a régua';
    return rejectWithValue(message);
  }
});

export const retomarCobranca = createAsyncThunk<void, string, { rejectValue: string }>(
  'reguaCobranca/retomarCobranca',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await reguaCobrancaService.retomar(id);
      await recarregarCobranca(id, dispatch);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao retomar a régua';
      return rejectWithValue(message);
    }
  },
);

export const registrarPagamentoCobranca = createAsyncThunk<void, string, { rejectValue: string }>(
  'reguaCobranca/registrarPagamentoCobranca',
  async (id, { dispatch, rejectWithValue }) => {
    try {
      await reguaCobrancaService.registrarPagamento(id);
      await recarregarCobranca(id, dispatch);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao confirmar o pagamento';
      return rejectWithValue(message);
    }
  },
);

export const encerrarCobranca = createAsyncThunk<
  void,
  { id: string; input: EncerrarCobrancaInput },
  { rejectValue: string }
>('reguaCobranca/encerrarCobranca', async ({ id, input }, { dispatch, rejectWithValue }) => {
  try {
    await reguaCobrancaService.encerrar(id, input);
    await recarregarCobranca(id, dispatch);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao encerrar a cobrança';
    return rejectWithValue(message);
  }
});

export const aplicarCobrancasEmLote = createAsyncThunk<
  number,
  { acao: CobrancaAcaoLote; ids: string[]; motivo?: string; detalhes?: string },
  { rejectValue: string }
>(
  'reguaCobranca/aplicarCobrancasEmLote',
  async ({ acao, ids, motivo, detalhes }, { rejectWithValue }) => {
    try {
      return await reguaCobrancaService.aplicarEmLote(acao, ids, motivo, detalhes);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao aplicar a ação em lote';
      return rejectWithValue(message);
    }
  },
);

export const registrarAcordoCobranca = createAsyncThunk<void, string, { rejectValue: string }>(
  'reguaCobranca/registrarAcordoCobranca',
  async (id, { rejectWithValue }) => {
    try {
      await reguaCobrancaService.registrarAcordo(id);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao registrar o acordo';
      return rejectWithValue(message);
    }
  },
);

export const exportarCobrancas = createAsyncThunk<
  string,
  ExportarCobrancasParams,
  { rejectValue: string }
>('reguaCobranca/exportarCobrancas', async (params, { rejectWithValue }) => {
  try {
    return await reguaCobrancaService.exportar(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao gerar a exportação';
    return rejectWithValue(message);
  }
});

export const duplicarReguaTemplate = createAsyncThunk<
  ReguaTemplate,
  { id: string; payload: DuplicarReguaPayload },
  { rejectValue: string }
>('reguaCobranca/duplicarTemplate', async ({ id, payload }, { rejectWithValue }) => {
  try {
    return await reguaTemplateService.duplicar(id, payload);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao duplicar régua de cobrança';
    return rejectWithValue(message);
  }
});

export const excluirReguaTemplate = createAsyncThunk<string, string, { rejectValue: string }>(
  'reguaCobranca/excluirTemplate',
  async (id, { rejectWithValue }) => {
    try {
      await reguaTemplateService.excluir(id);
      return id;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao excluir régua de cobrança';
      return rejectWithValue(message);
    }
  },
);

export const criarLoteRascunho = createAsyncThunk<
  { loteId: string },
  { nome: string; reguaTemplateId: string },
  { rejectValue: string }
>('reguaCobranca/criarLoteRascunho', async ({ nome, reguaTemplateId }, { rejectWithValue }) => {
  try {
    const { loteId } = await reguaLoteService.criar({ nome });
    await reguaLoteService.configurar(loteId, { reguaTemplateId });
    return { loteId };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao criar lote de cobrança';
    return rejectWithValue(message);
  }
});

export const configurarLote = createAsyncThunk<
  void,
  { id: string; reguaTemplateId: string },
  { rejectValue: string }
>('reguaCobranca/configurarLote', async ({ id, reguaTemplateId }, { rejectWithValue }) => {
  try {
    await reguaLoteService.configurar(id, { reguaTemplateId });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao configurar lote de cobrança';
    return rejectWithValue(message);
  }
});

export const baixarModeloLote = createAsyncThunk<void, string, { rejectValue: string }>(
  'reguaCobranca/baixarModeloLote',
  async (loteId, { rejectWithValue }) => {
    try {
      await downloadBlob(`/cobranca/lotes/${loteId}/modelo`, 'modelo-cobranca.xlsx');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Erro ao baixar modelo';
      return rejectWithValue(message);
    }
  },
);

export const uploadLote = createAsyncThunk<
  { loteId: string },
  { id: string; file: File; mapeamento?: MapeamentoLoteDto; ignoreRows?: number[] },
  { rejectValue: UploadLoteRejectPayload }
>('reguaCobranca/uploadLote', async (params, { rejectWithValue }) => {
  try {
    return await reguaLoteService.upload(params);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao enviar planilha';
    const augmented = error as Partial<SpreadsheetValidationError>;
    if (augmented.code === SPREADSHEET_VALIDATION_FAILED) {
      return rejectWithValue({
        code: SPREADSHEET_VALIDATION_FAILED,
        issues: augmented.issues ?? [],
        hasCritical: augmented.hasCritical ?? true,
        totalRows: augmented.totalRows,
        message,
      });
    }
    return rejectWithValue({ message });
  }
});

export const fetchLoteItens = createAsyncThunk<
  { items: LoteItem[]; total: number; corrigiveis: number; page: number; limit: number },
  { id: string; page: number; limit: number; status?: LoteItemStatus },
  { rejectValue: string }
>('reguaCobranca/fetchLoteItens', async ({ id, page, limit, status }, { rejectWithValue }) => {
  try {
    return await reguaLoteService.listarItens(id, { page, limit, status });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao carregar itens do lote';
    return rejectWithValue(message);
  }
});

export const atualizarItemLote = createAsyncThunk<
  void,
  { itemId: string; input: EditarLoteItemInput },
  { rejectValue: string }
>('reguaCobranca/atualizarItemLote', async ({ itemId, input }, { rejectWithValue }) => {
  try {
    await reguaLoteService.atualizarItem(itemId, input);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao atualizar item';
    return rejectWithValue(message);
  }
});

export const removerItemLote = createAsyncThunk<
  void,
  { id: string; itemId: string },
  { rejectValue: string }
>('reguaCobranca/removerItemLote', async ({ id, itemId }, { rejectWithValue }) => {
  try {
    await reguaLoteService.removerItem(id, itemId);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao remover item';
    return rejectWithValue(message);
  }
});

export const validarInicioLote = createAsyncThunk<
  ValidarInicioResult,
  string,
  { rejectValue: string }
>('reguaCobranca/validarInicioLote', async (id, { rejectWithValue }) => {
  try {
    return await reguaLoteService.validarInicio(id);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao validar início do lote';
    return rejectWithValue(message);
  }
});

export const corrigirNomesLote = createAsyncThunk<
  CorrecaoNomesLoteResult,
  string,
  { rejectValue: string }
>('reguaCobranca/corrigirNomesLote', async (id, { rejectWithValue }) => {
  try {
    return await reguaLoteService.corrigirNomes(id);
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao corrigir nomes';
    return rejectWithValue(message);
  }
});

export const iniciarLote = createAsyncThunk<
  IniciarLoteResult,
  { id: string; aceiteSedex: boolean },
  { rejectValue: string }
>('reguaCobranca/iniciarLote', async ({ id, aceiteSedex }, { rejectWithValue }) => {
  try {
    return await reguaLoteService.iniciar(id, { aceiteSedex });
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Erro ao iniciar lote';
    return rejectWithValue(message);
  }
});
